import {
  TUNABLE_FIELDS,
  TUNABLE_PRESETS,
  applyTunablePreset,
  exportTunables,
  getTunableValue,
  importTunables,
  resetTunables,
  setTunableValue,
} from '../config/tunables';
import { BOSS_TYPES, BossType } from './bossCatalog';

const VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.0';

export class DevTuningMenu {
  private root?: HTMLDivElement;
  private panel?: HTMLDivElement;
  private bossSelect?: HTMLSelectElement;
  private outsidePointerHandler?: (event: PointerEvent) => void;

  mount() {
    if (this.root) {
      return;
    }

    this.root = document.createElement('div');
    this.root.className = 'dev-tuning-root';

    const button = document.createElement('button');
    button.className = 'dev-tuning-toggle';
    button.textContent = '调参';
    button.title = `调参 ${VERSION}`;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      this.togglePanel();
    });

    this.panel = document.createElement('div');
    this.panel.className = 'dev-tuning-panel';
    this.panel.hidden = true;
    this.panel.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.renderPanel();

    this.root.append(button, this.panel);
    document.body.appendChild(this.root);

    this.outsidePointerHandler = (event: PointerEvent) => {
      if (!this.panel || this.panel.hidden || !this.root) {
        return;
      }

      const target = event.target;
      if (target instanceof Node && this.root.contains(target)) {
        return;
      }

      this.panel.hidden = true;
    };
    document.addEventListener('pointerdown', this.outsidePointerHandler, true);
  }

  destroy() {
    if (this.outsidePointerHandler) {
      document.removeEventListener('pointerdown', this.outsidePointerHandler, true);
      this.outsidePointerHandler = undefined;
    }
    this.root?.remove();
    this.root = undefined;
    this.panel = undefined;
  }

  private togglePanel() {
    if (!this.panel) {
      return;
    }
    this.panel.hidden = !this.panel.hidden;
  }

  private renderPanel(message = '') {
    if (!this.panel) {
      return;
    }

    this.panel.replaceChildren();
    const title = document.createElement('div');
    title.className = 'dev-tuning-title';
    const titleText = document.createElement('span');
    titleText.textContent = `开发者参数 v${VERSION}`;
    const close = document.createElement('button');
    close.className = 'dev-tuning-close';
    close.type = 'button';
    close.textContent = '关闭';
    close.addEventListener('click', () => {
      this.panel!.hidden = true;
    });
    title.append(titleText, close);
    this.panel.append(title);
    this.renderPresetControls();

    const groups = new Map<string, typeof TUNABLE_FIELDS>();
    for (const field of TUNABLE_FIELDS) {
      const groupName = field.path.split('.')[0];
      groups.set(groupName, [...(groups.get(groupName) ?? []), field]);
    }

    for (const [groupName, fields] of groups) {
      const section = document.createElement('section');
      const heading = document.createElement('h3');
      heading.textContent = groupName;
      section.append(heading);

      for (const field of fields) {
        const value = getTunableValue(field.path);
        const row = document.createElement('label');
        row.className = 'dev-tuning-row';

        const label = document.createElement('span');
        label.textContent = field.label;

        const range = document.createElement('input');
        range.type = 'range';
        range.min = String(field.min);
        range.max = String(field.max);
        range.step = String(field.step);
        range.value = String(value);

        const number = document.createElement('input');
        number.type = 'number';
        number.min = String(field.min);
        number.max = String(field.max);
        number.step = String(field.step);
        number.value = String(value);

        const update = (raw: string) => {
          const next = Number(raw);
          setTunableValue(field.path, next);
          const updated = getTunableValue(field.path);
          range.value = String(updated);
          number.value = String(updated);
        };

        range.addEventListener('input', () => update(range.value));
        number.addEventListener('change', () => update(number.value));

        row.append(label, range, number);
        section.append(row);
      }
      this.panel.append(section);
    }

    const actions = document.createElement('div');
    actions.className = 'dev-tuning-actions';

    const copy = document.createElement('button');
    copy.textContent = '复制JSON';
    copy.addEventListener('click', async () => {
      await navigator.clipboard.writeText(exportTunables());
      this.renderPanel('已复制');
    });

    const paste = document.createElement('button');
    paste.textContent = '导入JSON';
    paste.addEventListener('click', () => {
      const raw = window.prompt('粘贴调参 JSON');
      if (!raw) {
        return;
      }
      importTunables(raw);
      this.renderPanel('已导入');
    });

    const reset = document.createElement('button');
    reset.textContent = '重置';
    reset.addEventListener('click', () => {
      resetTunables();
      this.renderPanel('已重置');
    });

    actions.append(copy, paste, reset);
    this.panel.append(actions);
    this.renderBossLab();

    if (message) {
      const notice = document.createElement('div');
      notice.className = 'dev-tuning-notice';
      notice.textContent = message;
      this.panel.append(notice);
    }
  }

  private renderPresetControls() {
    if (!this.panel) {
      return;
    }

    const section = document.createElement('section');
    section.className = 'dev-tuning-presets';
    const heading = document.createElement('h3');
    heading.textContent = '预设难度';
    heading.style.color = '#ffb366';
    heading.style.textAlign = 'center';
    heading.style.marginBottom = '12px';
    section.append(heading);

    const difficultyContainer = document.createElement('div');
    difficultyContainer.style.display = 'flex';
    difficultyContainer.style.gap = '8px';
    difficultyContainer.style.marginBottom = '16px';
    difficultyContainer.style.justifyContent = 'center';

    const otherActions = document.createElement('div');
    otherActions.className = 'dev-tuning-actions dev-tuning-preset-actions';

    const scanHeading = document.createElement('h3');
    scanHeading.textContent = '🔬 扫描验证预设';
    scanHeading.style.color = '#7dfaab';
    scanHeading.style.textAlign = 'center';
    scanHeading.style.marginTop = '16px';
    scanHeading.style.marginBottom = '12px';

    const scanContainer = document.createElement('div');
    scanContainer.style.display = 'flex';
    scanContainer.style.gap = '8px';
    scanContainer.style.marginBottom = '16px';
    scanContainer.style.justifyContent = 'center';

    for (const preset of TUNABLE_PRESETS) {
      if (preset.id.startsWith('difficulty-')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = preset.label;
        button.title = preset.description;
        
        button.style.flex = '1';
        button.style.padding = '12px 0';
        button.style.fontSize = '22px';
        button.style.fontWeight = '900';
        button.style.borderRadius = '8px';
        
        let color = '#ccc';
        if (preset.label === '1') color = '#a8ffb2';
        if (preset.label === '2') color = '#a8d8ff';
        if (preset.label === '3') color = '#f7df9a';
        if (preset.label === '4') color = '#ff9e5e';
        if (preset.label === '5') color = '#ff5d4f';
        
        button.style.backgroundColor = '#1a1612';
        button.style.color = color;
        button.style.border = `2px solid ${color}`;
        button.style.cursor = 'pointer';
        button.style.boxShadow = `0 4px 10px rgba(0,0,0,0.5), inset 0 0 10px ${color}40`;

        button.addEventListener('pointerover', () => { button.style.backgroundColor = '#2a241e'; });
        button.addEventListener('pointerout', () => { button.style.backgroundColor = '#1a1612'; });

        button.addEventListener('click', () => {
          applyTunablePreset(preset.id);
          this.renderPanel(`✅ 已切换至难度 ${preset.label}`);
        });
        difficultyContainer.append(button);
      } else if (preset.id.startsWith('scan-')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = preset.label;
        button.title = preset.description;
        
        button.style.flex = '1';
        button.style.padding = '10px 4px';
        button.style.fontSize = '12px';
        button.style.fontWeight = 'bold';
        button.style.borderRadius = '8px';
        button.style.backgroundColor = '#102517';
        button.style.color = '#7dfaab';
        button.style.border = `2px solid #7dfaab`;
        button.style.cursor = 'pointer';
        
        button.addEventListener('pointerover', () => { button.style.backgroundColor = '#1f452a'; });
        button.addEventListener('pointerout', () => { button.style.backgroundColor = '#102517'; });

        button.addEventListener('click', () => {
          applyTunablePreset(preset.id);
          this.renderPanel(`✅ 已应用验证预设: ${preset.label}`);
        });
        scanContainer.append(button);
      } else {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = preset.label;
        button.title = preset.description;
        button.addEventListener('click', () => {
          applyTunablePreset(preset.id);
          this.renderPanel(`已应用 ${preset.label}`);
        });
        otherActions.append(button);
      }
    }

    section.append(difficultyContainer);
    if (scanContainer.children.length > 0) {
      section.append(scanHeading);
      section.append(scanContainer);
    }

    const random = document.createElement('button');
    random.type = 'button';
    random.textContent = '全随机';
    random.title = '围绕默认参数随机扰动，适合寻找新手感。';
    random.addEventListener('click', () => {
      applyTunablePreset('random');
      this.renderPanel('已应用 全随机');
    });
    otherActions.append(random);

    section.append(otherActions);
    this.panel.append(section);
  }

  private renderBossLab() {
    if (!this.panel) {
      return;
    }

    const section = document.createElement('section');
    section.className = 'dev-boss-lab';
    const heading = document.createElement('h3');
    heading.textContent = 'Boss实验室';
    section.append(heading);

    const select = document.createElement('select');
    select.className = 'dev-boss-select';
    for (const type of BOSS_TYPES) {
      const option = document.createElement('option');
      option.value = type;
      option.textContent = type;
      select.append(option);
    }
    this.bossSelect = select;
    section.append(select);

    const actions = document.createElement('div');
    actions.className = 'dev-tuning-actions';
    const spawnOne = this.createBossButton('生成一个', () => this.spawnBoss(1));
    const spawnThree = this.createBossButton('生成三个', () => this.spawnBoss(3));
    const dummy = this.createBossButton('训练木桩', () => this.spawnBoss(1, 'training-dummy'));
    const clear = this.createBossButton('清除Boss', () => window.animeBrotatoBossLab?.clear());
    actions.append(spawnOne, spawnThree, dummy, clear);
    section.append(actions);
    this.panel.append(section);
  }

  private createBossButton(label: string, action: () => void) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', action);
    return button;
  }

  private spawnBoss(count: number, forcedType?: BossType) {
    const type = forcedType ?? (this.bossSelect?.value as BossType | undefined);
    if (!type) {
      throw new Error('Boss type is not selected');
    }
    window.animeBrotatoBossLab?.setMultipliers(
      getTunableValue('bossLab.hpMultiplier'),
      getTunableValue('bossLab.speedMultiplier'),
    );
    window.animeBrotatoBossLab?.spawn(type, count);
  }
}

declare global {
  interface Window {
    animeBrotatoBossLab?: {
      spawn: (type: BossType, count?: number) => unknown;
      clear: () => void;
      setMultipliers: (hpMultiplier: number, speedMultiplier: number) => void;
    };
  }
}
